import { useState, useEffect } from 'react'
import type { SortingState, VisibilityState } from '@tanstack/react-table'
import { useDebounce } from '@/client/hooks/useDebounce'
import { useCrawlPages, type CrawlPage } from '../hooks/useCrawls'
import { DataTable } from './DataTable'
import { PageDetailSheet } from './PageDetailSheet'
import { pageColumns, DEFAULT_COLUMN_VISIBILITY } from './columns'

const STORAGE_KEY = 'ww-page-columns'

export function DataTab({ crawlId }: { crawlId: string }) {
  const [search, setSearch] = useState('')
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null)
  const debouncedSearch = useDebounce(search, 300)

  const [sorting, setSorting] = useState<SortingState>([{ id: 'url', desc: false }])
  const [pageIndex, setPageIndex] = useState(0)
  const [pageSize, setPageSize] = useState(50)

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      return saved ? JSON.parse(saved) : { ...DEFAULT_COLUMN_VISIBILITY }
    } catch {
      return { ...DEFAULT_COLUMN_VISIBILITY }
    }
  })

  // Persist column visibility to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(columnVisibility))
  }, [columnVisibility])

  // Reset to page 0 when search changes
  useEffect(() => {
    setPageIndex(0)
  }, [debouncedSearch])

  const sortCol = sorting[0]
  const { data, isLoading } = useCrawlPages(crawlId, {
    search: debouncedSearch,
    limit: pageSize,
    offset: pageIndex * pageSize,
    sortBy: sortCol?.id || 'url',
    sortDir: sortCol?.desc ? 'desc' : 'asc',
  })

  return (
    <>
      <DataTable
        columns={pageColumns}
        data={data?.pages || []}
        total={data?.total || 0}
        pageSize={pageSize}
        pageIndex={pageIndex}
        onPaginationChange={(idx, size) => {
          setPageIndex(idx)
          setPageSize(size)
        }}
        sorting={sorting}
        onSortingChange={setSorting}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={setColumnVisibility}
        isLoading={isLoading}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search pages by URL..."
        onRowClick={(row: CrawlPage) => setSelectedPageId(row.id)}
      />
      <PageDetailSheet
        crawlId={crawlId}
        pageId={selectedPageId}
        onClose={() => setSelectedPageId(null)}
      />
    </>
  )
}
